import { useState } from 'react';
import { BookCheck, CheckCircle2, CircleDashed, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import { useSetExpenseStatus } from './hooks';
import type { ExpenseStatus, QbPostStatus } from '@/types/database';

/**
 * Shared visual + edit primitives for `expense_status`. Lives in
 * `features/expenses` because every expense surface (My Expenses, Live
 * Transactions, Transaction Detail, Expense Detail) renders one of these
 * — they used to live inside the page that bound them.
 */

/**
 * The "Posted" badge takes precedence over `expense_status` — once QBO
 * has accepted the post, that's the headline state for the row. We use
 * a ShieldCheck instead of the plain CheckCircle to visually distinguish
 * "approved (still in Atlas)" from "posted (live in QuickBooks)".
 */
export function ExpenseStatusBadge({
  status,
  qbPostStatus,
}: {
  status: ExpenseStatus;
  qbPostStatus?: QbPostStatus | null;
}) {
  if (qbPostStatus === 'posted') {
    return (
      <Badge variant="success" className="gap-1" title="Posted to QuickBooks">
        <ShieldCheck className="h-3 w-3" />
        Posted
      </Badge>
    );
  }
  switch (status) {
    case 'approved':
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </Badge>
      );
    case 'submitted':
      return (
        <Badge variant="info" className="gap-1">
          <CircleDashed className="h-3 w-3" />
          Submitted
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="warning" className="gap-1">
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    case 'in_qb':
      // "Already in QuickBooks" — booked in QBO outside Atlas (manual
      // Purchase, journal entry, CSV import). The qb_status_sync trigger
      // pins qb_post_status='skipped' so the Post button is inert. We
      // pick a muted, BookCheck-iconed pill so the eye reads it as a
      // settled state rather than something the operator still owes.
      return (
        <Badge variant="muted" className="gap-1" title="Already booked in QuickBooks outside Atlas — Atlas will not post this row.">
          <BookCheck className="h-3 w-3" />
          Already in QB
        </Badge>
      );
    default:
      return <Badge variant="muted">Draft</Badge>;
  }
}

/**
 * Inline expense_status picker for super_admin. Shows the readonly badge
 * for everyone else (the trigger guard would reject their write anyway).
 *
 * Picking "Rejected" opens a small dialog asking for a reason — the
 * cardholder will see it on their detail page. Every other transition
 * fires immediately. We stop click-propagation on the wrapper so picking
 * a status from inside a clickable table row doesn't also navigate to
 * the detail page.
 */
export function ExpenseStatusEditor({
  transactionId,
  status,
  qbPostStatus,
  className,
}: {
  transactionId: string;
  status: ExpenseStatus;
  qbPostStatus?: QbPostStatus | null;
  className?: string;
}) {
  const { role } = useAuth();
  const superAdmin = isSuperAdmin(role);
  const setStatus = useSetExpenseStatus();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  if (!superAdmin) {
    return <ExpenseStatusBadge status={status} qbPostStatus={qbPostStatus} />;
  }
  // Once the row is live in QuickBooks we lock the editor — flipping
  // expense_status would silently desync from the QBO Purchase. To
  // change it the operator should void/delete in QBO first.
  if (qbPostStatus === 'posted') {
    return <ExpenseStatusBadge status={status} qbPostStatus={qbPostStatus} />;
  }

  const onChange = async (next: string) => {
    const target = next as ExpenseStatus;
    if (target === status) return;
    if (target === 'rejected') {
      setRejectReason('');
      setRejectOpen(true);
      return;
    }
    try {
      await setStatus.mutateAsync({ id: transactionId, status: target });
      toast.success(`Marked as ${labelForStatus(target).toLowerCase()}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Status change failed');
    }
  };

  const onConfirmReject = async () => {
    try {
      await setStatus.mutateAsync({
        id: transactionId,
        status: 'rejected',
        reason: rejectReason,
      });
      toast.success('Marked as rejected');
      setRejectOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not reject');
    }
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()} className={className}>
        <Select value={status} onValueChange={onChange} disabled={setStatus.isPending}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">
              <span className="flex items-center gap-1.5">
                Rejected
                <span className="text-xs text-muted-foreground">(reason required)</span>
              </span>
            </SelectItem>
            <SelectItem value="in_qb">
              <span className="flex items-center gap-1.5">
                Already in QB
                <span className="text-xs text-muted-foreground">(skip posting)</span>
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`reject-reason-${transactionId}`}>Reason</Label>
            <Textarea
              id={`reject-reason-${transactionId}`}
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Receipt is unreadable. Please upload a clearer copy."
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmReject}
              disabled={!rejectReason.trim() || setStatus.isPending}
            >
              {setStatus.isPending ? 'Rejecting…' : 'Reject expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function labelForStatus(status: ExpenseStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'submitted':
      return 'Submitted';
    case 'rejected':
      return 'Rejected';
    case 'in_qb':
      return 'Already in QB';
    default:
      return 'Draft';
  }
}
